/**
 * Generate a stable clientId that persists across page refreshes within the same tab
 * 
 * Strategy:
 * - Generate a unique tab instance ID on first load (random, in-memory only)
 * - Store clientId in localStorage with diagram + tab instance as key
 * - On refresh, same tab instance ID is used (via performance API detection)
 * - Different tabs get different tab instance IDs → different clientIds
 * 
 * This prevents false "new client" detections during page refresh
 * while still allowing multi-tab for the same user.
 */

// Generate a unique identifier for this tab instance (survives refresh via sessionStorage)
let tabInstanceId: string | null = null;

function getOrCreateTabInstanceId(): string {
  if (tabInstanceId) {
    return tabInstanceId;
  }

  const TAB_INSTANCE_KEY = 'creately_tab_instance_id';

  try {
    // Try to get existing tab instance ID from sessionStorage
    // sessionStorage is tab-specific and persists across refreshes
    const existing = sessionStorage.getItem(TAB_INSTANCE_KEY);
    if (existing) {
      tabInstanceId = existing;
      return tabInstanceId;
    }
  } catch (err) {
    console.warn('[StableClientId] sessionStorage not available:', err);
  }

  // Generate new unique tab instance ID
  // Use crypto.randomUUID if available for true uniqueness, fallback to timestamp + random
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    tabInstanceId = crypto.randomUUID();
  } else {
    // Fallback: use more random bits to reduce collision probability
    tabInstanceId = `${Date.now()}_${Math.random().toString(36).substr(2, 16)}_${Math.random().toString(36).substr(2, 16)}`;
  }

  // Store in sessionStorage for this tab (survives refresh, isolated per tab)
  try {
    sessionStorage.setItem(TAB_INSTANCE_KEY, tabInstanceId);
  } catch (err) {
    console.warn('[StableClientId] Failed to store in sessionStorage:', err);
  }

  return tabInstanceId;
}

export function getStableTabClientId(diagramId: string): number {
  const tabId = getOrCreateTabInstanceId();
  const STORAGE_KEY = `creately_client_id_tab_${tabId}_${diagramId}`;

  try {
    // Try to get existing ID from localStorage
    const existing = localStorage.getItem(STORAGE_KEY);

    if (existing) {
      const parsed = parseInt(existing, 10);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }

    // Generate new random ID
    const newId = Math.floor(Math.random() * 2147483647);

    try {
      localStorage.setItem(STORAGE_KEY, String(newId));
    } catch (err) {
      console.error('[StableClientId] Failed to store in localStorage:', err);
    }

    return newId;
  } catch (err) {
    console.error('[StableClientId] localStorage error:', err);
    // Fallback: generate random ID (won't persist)
    const fallbackId = Math.floor(Math.random() * 2147483647);
    return fallbackId;
  }
}

/**
 * Clear the stable clientId (useful for testing or explicit logout)
 */
export function clearStableTabClientId(diagramId?: string): void {
  const tabId = tabInstanceId;
  if (!tabId) return;

  if (diagramId) {
    const STORAGE_KEY = `creately_client_id_tab_${tabId}_${diagramId}`;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('[StableClientId] Failed to clear:', err);
    }
  } else {
    // Clear all clientIds for this tab instance
    const allKeys = Object.keys(localStorage);
    const keysToRemove = allKeys.filter(k => k.includes(`_tab_${tabId}_`));
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
}

// Cleanup: Remove stale tab instance IDs on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Don't clean up - let localStorage persist for refresh detection
    // Stale entries will accumulate but are small and rare
  });
}

